import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ExternalLink,
  Sun,
  Moon,
  CheckCircle2,
  CalendarDays,
  MapPin,
  Building2,
  Wallet,
} from "lucide-react";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import bmLogo from "../assets/BM-Logo.png";

const STATUS_STYLES = {
  ROLLING: "text-brand border-brand/30 bg-brand/10",
  OPEN: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  "CLOSING SOON": "text-amber-400 border-amber-500/30 bg-amber-500/10",
  CLOSED: "text-rose-400 border-rose-500/30 bg-rose-500/10",
};

function formatDate(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function OpportunityDetail({ theme, toggleTheme }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/opportunities/${id}`)
      .then((res) => setItem(res.data))
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  const TopBar = (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-[900px] items-center justify-between gap-4 px-4 py-3">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="rounded-lg border border-border/70 bg-[#f15b58] p-[3px]">
            <img src={bmLogo} alt="BuilderMantra" className="h-7 w-7 rounded-md object-cover" />
          </div>
          <span className="text-[17px] font-bold tracking-tight text-foreground">
            buildermantra<span className="text-brand">.com</span>
          </span>
        </Link>
        <button
          onClick={toggleTheme}
          aria-label="Toggle theme"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-secondary/50 text-foreground transition-colors hover:bg-secondary"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        {TopBar}
        <div className="mx-auto max-w-[900px] px-4 py-24 text-center text-muted-foreground">
          Loading...
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-background">
        {TopBar}
        <div className="mx-auto max-w-[900px] px-4 py-24 text-center">
          <p className="text-lg text-foreground">Opportunity not found.</p>
          <Link to="/funding" className="mt-4 inline-block text-brand hover:underline">
            &larr; Back to all opportunities
          </Link>
        </div>
      </div>
    );
  }

  const statusClass = STATUS_STYLES[item.status] || STATUS_STYLES.ROLLING;
  const open = formatDate(item.openDate);
  const close = formatDate(item.closeDate);

  return (
    <div className="min-h-screen bg-background">
      {TopBar}

      <main className="mx-auto max-w-[900px] px-4 pb-24">
        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex items-center gap-1 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {/* Header block */}
        <div className="mt-4 flex flex-col gap-5 border-b border-border/60 pb-8 sm:flex-row sm:items-start">
          <div className="flex items-start gap-4">
            <Logo logoUrl={item.logoUrl} domain={item.domain} title={item.title} size={64} />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">{item.title}</h1>
                <span
                  className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusClass}`}
                >
                  {item.status}
                </span>
              </div>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-block text-[14px] text-muted-foreground hover:text-foreground"
              >
                {item.domain}
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:ml-auto">
            <Button asChild className="h-11 bg-brand px-5 text-brand-foreground hover:bg-brand/90">
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                Apply <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        {/* Meta grid */}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MetaItem icon={Building2} label="Organizer" value={item.org} />
          <MetaItem icon={MapPin} label="Region" value={item.country || item.region} />
          <MetaItem icon={Wallet} label="Funding" value={item.fundingType || item.category} />
          <MetaItem
            icon={CalendarDays}
            label="Deadline"
            value={close || (item.status === "ROLLING" ? "Rolling" : "—")}
          />
        </div>

        {/* About */}
        <section className="mt-8">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            About
          </h2>
          <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-foreground/90">
            {item.fullDescription || item.description}
          </p>
        </section>

        {/* Benefits */}
        {item.benefits && item.benefits.length > 0 && (
          <section className="mt-8">
            <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
              What you get
            </h2>
            <ul className="mt-3 space-y-2">
              {item.benefits.map((b, i) => (
                <li key={i} className="flex gap-2.5 text-[14.5px] leading-relaxed text-foreground/90">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Eligibility */}
        {item.eligibility && (
          <section className="mt-8">
            <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
              Eligibility
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-foreground/90">
              {item.eligibility}
            </p>
          </section>
        )}

        {/* Timeline */}
        {(open || close) && (
          <section className="mt-8">
            <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
              Timeline
            </h2>
            <div className="mt-3 flex flex-wrap gap-6 text-[14.5px] text-foreground/90">
              {open && (
                <div>
                  <div className="text-muted-foreground">Opens</div>
                  <div className="font-medium">{open}</div>
                </div>
              )}
              {close && (
                <div>
                  <div className="text-muted-foreground">Closes</div>
                  <div className="font-medium">{close}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <section className="mt-8">
            <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
              Tags
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-[12.5px] text-foreground/80"
                >
                  {t}
                </span>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3">
      <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-[14px] font-medium text-foreground">{value}</div>
    </div>
  );
}
